import React, { useEffect, useState } from 'react';
import { Card, Button } from "react-bootstrap";
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getCompanyIdFromRoute } from 'services/redux/selectors/company';
import DataTableManual from 'components/Common/DataTableManual/DataTableManual';
import Icon from 'components/Icons/Icon';
import conxtdOut from 'apis/conxtdOut';

import { 
  loadCompanyAssociatedEnterprisesStart,
  loadCompanyUnassociatedEnterprisesStart,
  loadCompanyDeletedEnterprisesStart,
} from 'services/redux/actions/serviceDesk';
import Alert from 'components/Common/Alert';
import Loading from 'components/Loading';
import DataTableManualCustom from './AssetEditor/CompanyAssetLogs/DataTableManualCustom';

function ManageEnterpriseAssociations() {

  const dispatch = useDispatch();
  const isPopulatedArray = (arr) => Array.isArray(arr) && arr.length > 0;

  // selectors
  const companyId = useSelector(getCompanyIdFromRoute);

  // data selectors -->
  const associatedData = useSelector(state => state.serviceDesk.manageEnterpriseAssociations?.associated?.data);
  const unassociatedData = useSelector(state => state.serviceDesk.manageEnterpriseAssociations?.unassociated?.data);
  const deletedData = useSelector(state => state.serviceDesk.manageEnterpriseAssociations?.deleted?.data);
  // ------------------------|
  // pagination selectors -->
  const associatedPagination = useSelector(state => state.serviceDesk.manageEnterpriseAssociations?.associated?.pagination);
  const unassociatedPagination = useSelector(state => state.serviceDesk.manageEnterpriseAssociations?.unassociated?.pagination);
  const deletedPagination = useSelector(state => state.serviceDesk.manageEnterpriseAssociations?.deleted?.pagination);
  // ------------------------|

  // handles storing pagination state for associated enterprises, unassociated enterprises, 
  // and deleted enterprises tables, into redux store
  const [associatedDataTableState, setAssociatedDataTableState] = useState({});
  const [deletedDataTableState, setDeletedDataTableState] = useState({});
    
  const handleAssociatedStorePaginationState = (dataTableState) => {
      setAssociatedDataTableState(dataTableState ?? {});
  }
  
  const handleDeletedStorePaginationState = (dataTableState) => {
      setDeletedDataTableState(dataTableState ?? {});
  }
  // -------------------------------------------------------

  // state
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [restoreLoadingId, setRestoreLoadingId] = useState(null);

  useEffect(() => {
    // fetch associated and unassociated enterprises on load
    if (!companyId) { return }

    // associated
    isPopulatedArray(associatedData) && dispatch(loadCompanyAssociatedEnterprisesStart({ companyId }));
    
    // unassociated
    isPopulatedArray(unassociatedData) && dispatch(loadCompanyUnassociatedEnterprisesStart({ companyId }));
    
    // deleted
    isPopulatedArray(deletedData) && dispatch(loadCompanyDeletedEnterprisesStart({ companyId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dispatch])

  const handleDeleteEnterprise = (id) => {
    if (!id) { return };
    
    Alert({
      text: 'Are you sure you want to delete this enterprise? This action cannot be undone.',
      icon: "warning",
      confirmButtonText: "Delete",
      showCancelButton: true,
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        setDeleteLoadingId(id);

        const deleteEnterprise = conxtdOut.post(`/ServiceDesk/deleteEnterprise/${Number(id)}`);
        
        deleteEnterprise
          .then(() => {
            setDeleteLoadingId(null);
            dispatch(loadCompanyAssociatedEnterprisesStart({ companyId, ...associatedDataTableState }))
            dispatch(loadCompanyDeletedEnterprisesStart({ companyId, ...deletedDataTableState }))
            Alert({
              text: 'Deleted Enterprise Successfully',
              icon: "success",
              timer: 3000,
            }).then(result => {
              if (result.value) {
                return;
              }
            })
          })
          .catch((error) => {
            setDeleteLoadingId(null);
            console.log('error: ', error.response?.data?.message);
            Alert({
              text: error.response?.data?.message || 'Something went wrong when deleting this enterprise. Please try again later, or reach out to a member of our team',
              icon: "error",
              // showCancelButton: true,
              // cancelButtonText: "Dismiss",
              timer: 3000,
            }).then(result => {
              if (result.value) {
                return;
              }
            })
          })
      } else {
        return;
      }
    })
  }
  
  const handleRestoreEnterprise = (id) => {
    if (!id) { return };
    
    Alert({
      text: 'Are you sure you want to restore this enterprise?',
      icon: "warning",
      confirmButtonText: "Restore Enterprise",
      showCancelButton: true,
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        setRestoreLoadingId(id);

        const deleteEnterprise = conxtdOut.post(`/ServiceDesk/restoreDeletedEnterprise/${Number(id)}`);
        
        deleteEnterprise
          .then(() => {
            setRestoreLoadingId(null);
            dispatch(loadCompanyAssociatedEnterprisesStart({ companyId, ...associatedDataTableState }))
            dispatch(loadCompanyDeletedEnterprisesStart({ companyId, ...deletedDataTableState }))
            Alert({
              text: 'Restored Enterprise Successfully',
              icon: "success",
              timer: 3000,
            }).then(result => {
              if (result.value) {
                return;
              }
            })
          })
          .catch((error) => {
            setRestoreLoadingId(null);
            console.log('error: ', error);
            Alert({
              text: 'Something went wrong when deleting this enterprise. Please try again later, or reach out to a member of our team',
              icon: "error",
              // showCancelButton: true,
              // cancelButtonText: "Dismiss",
              timer: 3000,
            }).then(result => {
              if (result.value) {
                return;
              }
            })
          })
      } else {
        return;
      }
    })
  }

  const enterprisesColumns = [
    {
      Header: "Enterprise Name",
      accessor: "name",
      minWidth: 200,
      style: {
        textAlign: "left",
      },
    },
    {
      Header: "No. of Sites",
      accessor: "count",
      filterable: false,
      maxWidth: 120,
      style: {
        textAlign: "left",
      },
      Cell: ({ row }) => {
        return (
          <span className='d-flex align-items-center justify-content-center'>
            {row._original?.count}
          </span>
        )
      },
    },
    {
      Header: "Actions",
      filterable: false,
      maxWidth: 100,
      Cell: ({ row }) => {
        // const rowData = row;
        // console.log(rowData)
        return (
          <span className='d-flex align-items-center justify-content-center'>
            {deleteLoadingId === row._original?.enterprise_id ? 
              <Loading /> 
            :  
              <Button
                variant="danger" 
                onClick={() => handleDeleteEnterprise(row._original?.enterprise_id)}
                data-intercom-target={`delete-enterprise-button`}
              >
              <Icon className='fas fa-trash' />
            </Button>}
          </span>
        )
      }
    },
  ];
  
  const softDeleteColumns = [
    {
      Header: "Enterprise Name",
      accessor: "name",
      minWidth: 200,
      style: {
        textAlign: "left",
      },
    },
    {
      Header: "No. of Sites",
      accessor: "count",
      filterable: false,
      maxWidth: 120,
      style: {
        textAlign: "left",
      },
      Cell: ({ row }) => {
        return (
          <span className='d-flex align-items-center justify-content-center'>
            {row._original?.count}
          </span>
        )
      },
    },
    {
      Header: "Actions",
      filterable: false,
      maxWidth: 100,
      Cell: ({ row }) => {
        // const rowData = row;
        // console.log(rowData)
        return (
          <span className='d-flex align-items-center justify-content-center'>
            {restoreLoadingId === row._original?.enterprise_id ? 
              <Loading /> 
            :  
              <Button
                variant="warning" 
                onClick={() => handleRestoreEnterprise(row._original?.enterprise_id)}
                data-intercom-target={`delete-enterprise-button`}
              >
              <Icon className='fas fa-undo' />
            </Button>}
          </span>
        )
      }
    },
  ];

  return (
    <Card className="card-wrapper pt-2">
      <div className='card-header d-flex align-items-center justify-content-between px-1'>
        <Link to={`/companies/company/${companyId}`} className='back-to-sd pl-3'>
          <Icon className="fas fa-chevron-left mr-2" />
          {/* {!dateTimeFormatSmall &&  */}
          <p className='m-0'>Back to Service Desk</p>
          {/* } */}
        </Link>
        <h4 className="p-2 pr-3">Manage Enterprise Associations</h4>
      </div>
      <Card.Body>
        {companyId &&

          <>
            <div className='d-flex flex-column justify-content-start'>

              {/* Container for Associated and Soft Deleted Enterprises */}
                <div className='mb-4' style={{ maxWidth: '700px', overflow: 'auto' }} >
                  <div className='pl-1'>
                    <h6>Associated Enterprises</h6>
                    <p
                      style={{ whiteSpace: 'wrap', width: '700px', maxHeight: '40px', height: '40px', overflow: 'auto' }} 
                      className='d-flex text-wrap flex-wrap mb-0'
                    >
                      Enterprises that are associated with this company.
                    </p>
                  </div>

                  <DataTableManualCustom
                    id="company-enterprises-table-associated"
                    columns={enterprisesColumns}
                    data={associatedData}
                    defaultPageSize={5}
                    filterable={true}
                    pages={associatedPagination && associatedPagination.pageCount}
                    loadDataAction={loadCompanyAssociatedEnterprisesStart}
                    loadDataParams={{ companyId, ...associatedDataTableState }}
                    noDataText={"No associated enterprises found"}
                    handler={handleAssociatedStorePaginationState}
                    vScroll={'400'}
                    style={{
                      width: '700px',
                      // borderColor: '#00004d',
                      borderRadius: '3px',
                      borderWidth: '3px',
                      padding: '4px',
                    }}
                  />
                </div>

              <div className='mb-4' style={{ maxWidth: '700px', overflow: 'auto' }} >
                {/* Soft Deleted Enterprises */}
                <div className='pl-1'>
                  <h6>Soft Deleted Enterprises</h6>
                  <p
                    style={{ whiteSpace: 'wrap', width: '700px', maxHeight: '40px', height: '40px', overflow: 'auto' }} 
                    className='d-flex text-wrap flex-wrap'
                  >
                    Enterprises that have been set for deletion. Restore an Enterprise by selecting the restore action next to an Enterprise.
                  </p>
                </div>

                <DataTableManualCustom
                  id="company-enterprises-table-soft-deleted"
                  columns={softDeleteColumns}
                  data={deletedData}
                  defaultPageSize={5}
                  filterable={true}
                  pages={deletedPagination && deletedPagination.pageCount}
                  loadDataAction={loadCompanyDeletedEnterprisesStart}
                  loadDataParams={{ companyId, ...deletedDataTableState }}
                  noDataText={"No soft deleted enterprises found"}
                  handler={handleDeletedStorePaginationState}
                  vScroll={'400'}
                  style={{
                    width: '700px',
                    // borderColor: '#cc0000',
                    borderRadius: '3px',
                    borderWidth: '3px',
                    padding: '4px',
                  }}
                />
              </div>

              {/* <div className='mb-4' style={{ maxWidth: '700px', overflow: 'auto' }} >
                <div className='pl-1'>
                  <h6>Unassociated Enterprises</h6>
                  <p
                    style={{ whiteSpace: 'wrap', width: '700px', maxHeight: '40px', height: '40px', overflow: 'auto' }} 
                    className='d-flex text-wrap flex-wrap'
                  >
                    Enterprises that are <span className='font-weight-bold mx-1'>not</span> associated with this company.
                  </p>
                </div>
                
                <DataTableManual
                  id="company-enterprises-table-unassociated"
                  columns={enterprisesColumns.filter(c => c.Header !== 'Actions')}
                  data={unassociatedData}
                  defaultPageSize={5}
                  filterable={true}
                  pages={unassociatedPagination && unassociatedPagination.pageCount}
                  loadDataAction={loadCompanyUnassociatedEnterprisesStart}
                  loadDataParams={{ companyId }}
                  noDataText={"No unassociated enterprises found"}
                  vScroll={'400'}
                  style={{
                    width: '700px',
                    // borderColor: '#cc0000',
                    borderRadius: '3px',
                    borderWidth: '3px',
                    padding: '4px',
                  }}
                />
              </div> */}

            </div>
          </>
        }
      </Card.Body>
    </Card>
  )
}

export default ManageEnterpriseAssociations
